import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef } from "react";
import type { Submission } from "@/api/submissions.service";
import { getSubmissionPracticeFullName } from "@/utils/practiceName";
import { SubmissionFileLink } from "@/components/SubmissionFileLink";
import styles from "./DeleteSubmissionModal.module.css";
import { useDeleteSummary } from "./useDeleteSummary";

export type DeleteSubmissionModalProps = {
  submission: Submission | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
};

function valueOrNa(value?: string | null): string {
  return value === null || value === undefined || value === "" ? "N/A" : value;
}

/**
 * CS-only confirmation modal for force-deleting a submission (SOFTSUPP-312 Case 2).
 * Summarises the submission that is about to be permanently deleted — including any
 * uploaded document and the active-referee count — and warns that all of this data
 * is removed for good. On open it fetches the whole-submission delete summary so the
 * referee count is accurate across every entry (the list row only carries a per-entry
 * count, and the list is paginated, so client-side summing is unreliable). When the
 * submission covers multiple lawyers it additionally warns that all of them go.
 */
export function DeleteSubmissionModal({
  submission,
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
}: DeleteSubmissionModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    data: summary,
    loading: summaryLoading,
    error: summaryError,
  } = useDeleteSummary(submission?.submissionId, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isDeleting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isDeleting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;
    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  if (!isOpen || !submission) return null;

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isDeleting) {
      onClose();
    }
  }

  const entries = summary?.entries ?? [];
  const coversMultipleLawyers = entries.length > 1;

  const publicationLabel = submission.edition
    ? `${valueOrNa(submission.publicationName)} (${submission.edition})`
    : valueOrNa(submission.publicationName);

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-submission-title"
        aria-describedby="delete-submission-warning"
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.warningIcon} aria-hidden="true">
              <Icon icon="exclamation" size="large" />
            </div>
            <h2 id="delete-submission-title" className={styles.modalTitle}>
              Delete submission
            </h2>
          </div>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={isDeleting}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.submissionInfo}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Firm:</span>
              <span className={styles.infoValue}>{valueOrNa(submission.firmName)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Publication:</span>
              <span className={styles.infoValue}>{publicationLabel}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Practice area:</span>
              <span className={styles.infoValue}>{getSubmissionPracticeFullName(submission)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Region:</span>
              <span className={styles.infoValue}>{valueOrNa(submission.regionName)}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Document:</span>
              <span className={styles.infoValue}>
                <SubmissionFileLink
                  fileUri={submission.fileUri}
                  className={styles.fileLink}
                  fallback="No document uploaded"
                />
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Active referees:</span>
              <span className={styles.infoValue}>
                {summaryLoading ? (
                  <span className={styles.spinner} aria-label="Loading referee count" />
                ) : summaryError ? (
                  "—"
                ) : (
                  (summary?.activeRefereeCount ?? 0)
                )}
              </span>
            </div>
          </div>

          {summaryError && (
            <p className={styles.summaryError} role="alert">
              Could not load the submission summary. You can still delete, but the referee count and
              lawyer list may be incomplete.
            </p>
          )}

          {coversMultipleLawyers && (
            <div className={styles.warning} role="alert">
              <Icon icon="exclamation" size="small" />
              <div>
                <strong>
                  This submission covers {entries.length} lawyers — all of them will be deleted:
                </strong>
                <ul className={styles.lawyerList}>
                  {entries.map((entry, index) => (
                    <li key={`${entry.personName ?? "unnamed"}-${index}`}>
                      {entry.personName ?? "(no name)"}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div id="delete-submission-warning" className={styles.warning}>
            <Icon icon="exclamation" size="small" />
            <span>
              <strong>This action cannot be undone.</strong> The submission, its uploaded document,
              and all referees will be deleted.
            </span>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="default"
            onClick={onConfirm}
            disabled={isDeleting}
            className={styles.dangerButton}
          >
            {isDeleting ? (
              <>
                <span className={styles.spinner} />
                Deleting...
              </>
            ) : (
              "Delete submission"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
